import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { request } from "../api/client";
import { API_URLS } from "../utils/urls";

type ProfileState = {
  // User-chosen display name (UI only). Null means "use the Auth0 name".
  displayName: string | null;
  loaded: boolean;
};

const initialState: ProfileState = {
  displayName: null,
  loaded: false,
};

function normaliseDisplayName(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

/**
 * Loads the lightweight profile metadata (currently just the display name) into
 * Redux so app-wide UI (e.g. the Navbar) can show the user's chosen name.
 */
export const fetchProfileMeta = createAsyncThunk(
  "profile/fetchMeta",
  async (getAccessToken: (() => Promise<string>) | undefined, { rejectWithValue }) => {
    try {
      const data = await request(API_URLS.profile, {
        method: "GET",
        auth: true,
        ...(getAccessToken ? { getAccessToken } : {}),
      });
      return normaliseDisplayName(data?.fields?.displayName);
    } catch (error: unknown) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Failed to load profile"
      );
    }
  }
);

const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setDisplayName: (state, action) => {
      state.displayName = normaliseDisplayName(action.payload);
      state.loaded = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProfileMeta.fulfilled, (state, action) => {
        state.displayName = action.payload;
        state.loaded = true;
      })
      .addCase(fetchProfileMeta.rejected, (state) => {
        state.loaded = true;
      });
  },
});

export const { setDisplayName } = profileSlice.actions;
export default profileSlice.reducer;
